import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { cartRequest } from '@theme/fate-cart-sync';

// Consume the same server-rendered count used by the drawer. Never add raw cart.item_count.
export function countFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    if (typeof html !== 'string') continue;
    const match = html.match(/data-fate-cart-count=["'](\d+)["']/);
    if (match) return Number(match[1]);
  }
  return null;
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.refreshCartCount();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const count = countFromSections(event.detail?.data?.sections || event.detail?.resource?.sections);
    ++this.countRevision;
    if (count !== null) this.renderCartBubble(count, false);
    else this.refreshCartCount();
  };

  countRevision = 0;

  refreshCartCount = async () => {
    const revision = ++this.countRevision;
    const section = this.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
    if (!section) return;
    try {
      const response = await cartRequest((window.Shopify?.routes?.root || '/') + '?sections=' + encodeURIComponent(section));
      if (!response.ok) return;
      const count = countFromSections(await response.json());
      if (count !== null && revision === this.countRevision && this.isConnected) this.renderCartBubble(count, false, false);
    } catch (_) { /* Keep the last confirmed number when the network is unavailable. */ }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Absolute server count: product-form deltas and checkout extras never change the badge directly.

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;
    this.refs.cartBubble.dataset.fateCartCount = String(itemCount);
    const accessible = this.refs.cartBubbleText.querySelector('.visually-hidden');
    if (accessible) accessible.textContent = accessible.textContent.replace(/:\s*\d+\s*$/, ': ' + itemCount);

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);


    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;
    const value = Number(this.refs.cartBubble.dataset.fateCartCount);
    if (Number.isInteger(value) && value >= 0) this.renderCartBubble(value, false, false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
